import type { ReactElement, ReactNode } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';

import type { EditableEntity } from './EditableEntity';
import type { EditModel } from './EditModel';

import './EditDialog.scss';

interface EditDialogProps<T extends EditableEntity<Loaded>, Loaded> {
  readonly model: EditModel<T, Loaded>;
  readonly onSaved: () => void;
  readonly onClose: () => void;
  /**
   * Human-readable name of the entity, used in the title
   * ("Create <entityName>" / "Edit <entityName>") and in the load-error
   * message ("Failed to load <entityName>: ..."). Pass it in
   * Title-Case-As-Displayed; the load-error sentence lowercases it.
   */
  readonly entityName: string;
  /**
   * Render the entity's form fields. Called with the entity once it's
   * loaded; the body shows a spinner or error message otherwise.
   * Caller is responsible for wrapping the fields component in
   * `observer(...)` if it reads from MobX observables.
   */
  readonly children: (entity: T) => ReactNode;
}

/**
 * Generic Modal shell for EditModel-backed edit/create dialogs.
 * Handles the load → entity / error / spinner state machine, the
 * title and the standard Close / Save-and-Close footer buttons.
 * Concrete dialog components only render their entity-specific form
 * fields via the `children` render prop.
 */
export const EditDialog = observer(
  <T extends EditableEntity<Loaded>, Loaded>({ model, onSaved, onClose, entityName, children }: EditDialogProps<T, Loaded>): ReactElement => {
    const action = model.isCreating ? 'Create' : 'Edit';
    const title = `${action} ${entityName}`;

    return (
      <Modal show={true} onHide={() => model.cancel(onClose)} size="lg" centered backdrop="static" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="edit-dialog__body">
          {model.isLoading && (
            <div className="text-center p-4">
              <Spinner animation="border" role="status" />
            </div>
          )}
          {model.loadError && (
            <Alert variant="danger">
              Failed to load {entityName.toLowerCase()}: {model.loadError.message}
            </Alert>
          )}
          {model.entity && children(model.entity)}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => model.cancel(onClose)} disabled={model.isSaving}>
            <FontAwesomeIcon icon={faWindowClose} className="me-2" />
            Close
          </Button>
          <Button variant="primary" onClick={() => model.saveAndClose(onSaved)} disabled={model.entity === null || model.isSaving}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {model.isSaving ? 'Saving...' : 'Save and Close'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
);
